<template>
  <b-modal
    id="modal-tv-panel-config"
    :title="modalTitle"
    size="lg"
    modal-class="tv-panel-config-modal"
    body-class="tv-panel-config-body"
    hide-footer
    @hidden="onHidden"
  >
    <div v-if="loading" class="text-center py-4 text-muted">Carregando configurações…</div>
    <form v-else class="tv-config-form" @submit.prevent="save">
      <section class="config-section">
        <div class="config-section-header">
          <h6>Layout</h6>
        </div>
        <div class="config-grid config-grid-two">
          <div class="form-group">
            <label>Layout da tela</label>
            <select v-model="screenLayoutKey" class="form-control">
              <option
                v-for="preset in screenLayoutPresets"
                :key="preset.id"
                :value="preset.id"
              >
                {{ preset.label }}
              </option>
            </select>
            <small class="config-hint">{{ layoutHint }}</small>
          </div>
          <div class="form-group">
            <label>Máx. reservas por sala</label>
            <input
              v-model.number="form.max_reservations_per_room"
              type="number"
              min="0"
              max="10"
              class="form-control"
            />
          </div>
          <div class="form-group">
            <label>Modo do card</label>
            <select v-model="form.card_mode" class="form-control">
              <option value="expanded">Expandido (com horários)</option>
              <option value="compact">Compacto (sem detalhes)</option>
            </select>
          </div>
        </div>

        <div class="custom-control custom-switch config-check">
          <input
            id="tv-hide-empty-rooms"
            v-model="form.hide_rooms_without_reservation"
            type="checkbox"
            class="custom-control-input"
          />
          <label class="custom-control-label" for="tv-hide-empty-rooms">
            Ocultar salas sem reserva hoje
          </label>
        </div>
      </section>

      <section class="config-section">
        <div class="config-section-header">
          <h6>Rotação e atualização</h6>
        </div>
        <div class="config-grid config-grid-two">
          <div class="form-group">
            <label>Exibição das salas</label>
            <select v-model="form.display_mode" class="form-control">
              <option value="static">Estático (todas de uma vez)</option>
              <option value="rotating">Rotativo (pagina entre grupos)</option>
            </select>
          </div>
          <div class="form-group" v-show="form.display_mode === 'rotating'">
            <label>Intervalo de rotação</label>
            <select v-model.number="form.rotation_interval_seconds" class="form-control">
              <option :value="5">5 segundos</option>
              <option :value="10">10 segundos</option>
              <option :value="15">15 segundos</option>
              <option :value="20">20 segundos</option>
              <option :value="30">30 segundos</option>
            </select>
          </div>
          <div class="form-group">
            <label>Atualização dos dados</label>
            <select v-model.number="form.refresh_interval_seconds" class="form-control">
              <option :value="30">30 segundos</option>
              <option :value="60">1 minuto</option>
              <option :value="300">5 minutos</option>
            </select>
          </div>
        </div>
      </section>

      <section class="config-section">
        <div class="config-section-header">
          <h6>Cores e alertas</h6>
        </div>
        <div class="config-grid config-grid-three">
          <div class="form-group color-field">
            <label>Livre</label>
            <input v-model="form.color_free" type="color" class="form-control p-1" />
          </div>
          <div class="form-group color-field">
            <label>Ocupado</label>
            <input v-model="form.color_occupied" type="color" class="form-control p-1" />
          </div>
          <div class="form-group color-field">
            <label>Em breve</label>
            <input v-model="form.color_upcoming" type="color" class="form-control p-1" />
          </div>
        </div>
        <div class="form-group mb-0">
          <label>Alertar "em breve" se reserva começa em menos de (minutos)</label>
          <input
            v-model.number="form.upcoming_alert_minutes"
            type="number"
            min="1"
            max="120"
            class="form-control"
          />
        </div>
      </section>

      <section class="config-section">
        <div class="config-section-header">
          <h6>Identidade visual</h6>
        </div>
        <div class="config-grid config-grid-two align-items-start">
          <div class="check-group">
            <div class="custom-control custom-switch config-check">
              <input
                id="tv-show-logo"
                v-model="form.show_logo"
                type="checkbox"
                class="custom-control-input"
              />
              <label class="custom-control-label" for="tv-show-logo">Mostrar logo</label>
            </div>
            <div class="custom-control custom-switch config-check">
              <input
                id="tv-show-clock"
                v-model="form.show_clock"
                type="checkbox"
                class="custom-control-input"
              />
              <label class="custom-control-label" for="tv-show-clock">Mostrar relógio</label>
            </div>
          </div>
          <div class="form-group mb-0">
            <label>Tamanho do título</label>
            <select v-model="form.title_font_size" class="form-control">
              <option value="small">Pequeno</option>
              <option value="medium">Médio</option>
              <option value="large">Grande</option>
              <option value="xlarge">Extra grande</option>
            </select>
          </div>
        </div>
      </section>

      <div class="config-actions">
        <button type="button" class="btn btn-secondary mr-2" @click="close">Cancelar</button>
        <button type="submit" class="btn btn-add" :disabled="saving">
          {{ saving ? 'Salvando…' : 'Salvar configurações' }}
        </button>
      </div>
    </form>
  </b-modal>
</template>

<script>
const SCREEN_LAYOUT_PRESETS = [
  { id: "1", cols: 1, perPage: 1, label: "1 sala — tela cheia" },
  { id: "2", cols: 2, perPage: 2, label: "2 salas — lado a lado (50/50)" },
  { id: "4", cols: 2, perPage: 4, label: "4 salas — grade 2×2" },
  { id: "6", cols: 3, perPage: 6, label: "6 salas — grade 3×2" },
  { id: "8", cols: 4, perPage: 8, label: "8 salas — grade 4×2" },
  { id: "12", cols: 4, perPage: 12, label: "12 salas — grade 4×3" },
];

const defaultConfig = () => ({
  grid_columns: 3,
  max_reservations_per_room: 2,
  card_mode: "expanded",
  hide_rooms_without_reservation: false,
  display_mode: "rotating",
  rooms_per_rotation: 6,
  rotation_interval_seconds: 10,
  refresh_interval_seconds: 30,
  color_free: "#10b981",
  color_occupied: "#f43f5e",
  color_upcoming: "#f59e0b",
  upcoming_alert_minutes: 15,
  show_logo: true,
  show_clock: true,
  title_font_size: "large",
});

export default {
  name: "TvPanelConfigModal",
  data() {
    return {
      branchId: null,
      branchTitle: "",
      loading: false,
      saving: false,
      form: defaultConfig(),
    };
  },
  computed: {
    modalTitle() {
      if (this.branchTitle) {
        return "Configurar painel TV — " + this.branchTitle;
      }
      return "Configurar painel TV";
    },
    screenLayoutPresets() {
      return SCREEN_LAYOUT_PRESETS;
    },
    screenLayoutKey: {
      get() {
        const match = SCREEN_LAYOUT_PRESETS.find(
          (p) =>
            p.cols === this.form.grid_columns &&
            p.perPage === this.form.rooms_per_rotation
        );
        return match ? match.id : "6";
      },
      set(id) {
        const preset =
          SCREEN_LAYOUT_PRESETS.find((p) => p.id === id) ||
          SCREEN_LAYOUT_PRESETS.find((p) => p.id === "6");
        this.form.grid_columns = preset.cols;
        this.form.rooms_per_rotation = preset.perPage;
      },
    },
    layoutHint() {
      const preset = SCREEN_LAYOUT_PRESETS.find((p) => p.id === this.screenLayoutKey);
      if (!preset) {
        return "";
      }
      if (this.form.display_mode === "static") {
        return (
          "Modo estático: todas as salas na mesma tela, organizadas em até " +
          preset.cols +
          " colunas (grade " +
          preset.cols +
          "×" +
          Math.ceil(preset.perPage / preset.cols) +
          ")."
        );
      }
      return (
        "Modo rotativo: cada tela mostra até " +
        preset.perPage +
        " salas em grade " +
        preset.cols +
        "×" +
        Math.ceil(preset.perPage / preset.cols) +
        "."
      );
    },
  },
  methods: {
    toBoolean(value) {
      if (typeof value === "boolean") {
        return value;
      }
      if (value === 1 || value === "1" || value === "true") {
        return true;
      }
      return false;
    },
    mergeConfig(config) {
      const merged = Object.assign(defaultConfig(), config || {});
      merged.hide_rooms_without_reservation = this.toBoolean(
        merged.hide_rooms_without_reservation
      );
      merged.show_logo = this.toBoolean(merged.show_logo);
      merged.show_clock = this.toBoolean(merged.show_clock);
      return merged;
    },
    open(branchId, branchTitle) {
      this.branchId = branchId;
      this.branchTitle = branchTitle || "";
      this.form = defaultConfig();
      this.loading = true;
      this.$bvModal.show("modal-tv-panel-config");

      const api =
        this.$store.state.api + "branches/" + branchId + "/tv-panel-config";
      this.$http
        .get(api)
        .then((response) => {
          this.form = this.mergeConfig(response.data.config);
        })
        .catch(() => {
          this.$message(null, "Não foi possível carregar as configurações", "error");
          this.close();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    save() {
      const self = this;
      self.saving = true;
      const api =
        self.$store.state.api + "branches/" + self.branchId + "/tv-panel-config";

      self.$http
        .put(api, self.form)
        .then(() => {
          self.$message(null, "Configurações do painel TV salvas.", "success");
          self.close();
        })
        .catch((err) => {
          const msg =
            err.response && err.response.data && err.response.data.message
              ? err.response.data.message
              : "Não foi possível salvar as configurações";
          self.$message(null, msg, "error");
        })
        .finally(() => {
          self.saving = false;
        });
    },
    close() {
      this.$bvModal.hide("modal-tv-panel-config");
    },
    onHidden() {
      this.branchId = null;
      this.branchTitle = "";
      this.form = defaultConfig();
    },
  },
};
</script>

<style scoped>
.tv-config-form {
  margin: -4px -6px 0;
}

.config-section {
  padding: 18px 10px 20px;
  border-bottom: 1px solid #e7e7e7;
}

.config-section:first-child {
  padding-top: 8px;
}

.config-section-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.config-section-header h6 {
  margin: 0;
  color: #7b8190;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.config-grid {
  display: grid;
  gap: 18px 30px;
}

.config-grid-two {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.config-grid-three {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.form-group {
  margin-bottom: 18px;
}

.form-group label {
  margin-bottom: 7px;
  color: #202124;
  font-size: 14px;
  font-weight: 600;
}

.form-control {
  height: 38px;
}

.color-field .form-control {
  height: 34px;
}

.config-check {
  margin-top: 2px;
  font-weight: 600;
}

.config-hint {
  display: block;
  margin-top: 6px;
  color: #7b8190;
  font-size: 12px;
  line-height: 1.45;
}

.check-group {
  display: grid;
  gap: 12px;
  padding-top: 2px;
}

.config-actions {
  display: flex;
  justify-content: flex-end;
  padding: 18px 10px 4px;
}

@media (max-width: 767px) {
  .config-grid-two,
  .config-grid-three {
    grid-template-columns: 1fr;
  }

  .config-actions {
    flex-direction: column-reverse;
  }

  .config-actions .btn {
    width: 100%;
    margin: 0 0 10px !important;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Branch/TvPanelConfigModal.vue